/* ═══════════════════════════════════════════════════════════════════════
   VMFS SVG SHARED COMPONENTS
   Reusable SVG building blocks any machine renderer can call.

   Components included:
   - Color helpers + wrap colors
   - Defs (gradients, filters)
   - Top box (standard sign / LED / advertising screen)
   - Outdoor canopy + IP65 badge + side LEDs
   - Camera module
   - Connectivity badges (WiFi / 4G / Satellite)
   - Feet / wheels
   - VMFS logos (small + large)
   - Payment row (card + cash + QR)
   - Coin return cup
   - ID scanner panel (age verify — ID + NFC + face)
   - Delivery door (PUSH flap)

   Renderers reach these via:
     window.VMFS.shared.<componentName>(...)
   ═══════════════════════════════════════════════════════════════════════ */
(function(){
'use strict';

window.VMFS = window.VMFS || {};
const S = {};

/* ─── Color helpers ───────────────────────────────────────────────────── */
S.WRAP_COLORS = {
  navy:  '#1A56DB',
  red:   '#DC2626',
  green: '#16A34A',
  orange:'#EA580C',
  black: '#1F2937',
  white: '#FFFFFF'
};

S.darkerHex = function(hex, amt){
  const n = parseInt(String(hex).replace('#',''),16);
  const r = Math.max(0,(n>>16)-amt);
  const g = Math.max(0,((n>>8)&0xff)-amt);
  const b = Math.max(0,(n&0xff)-amt);
  return `rgb(${r},${g},${b})`;
};

S.getBodyColor = function(state){
  if(state.wrap === 'full' && state.wrapColor) return state.wrapColor;
  return '#FFFFFF';
};

S.getBodyEdgeColor = function(state){
  if(state.wrap === 'full' && state.wrapColor){
    return S.darkerHex(state.wrapColor, 40);
  }
  return '#D1D5DB';
};

/* ─── Shared <defs> — gradients + filters ─────────────────────────────── */
S.buildDefs = function(){
  return `<defs>
    <linearGradient id="bodyShell" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%"   stop-color="#FFFFFF"/>
      <stop offset="50%"  stop-color="#F9FAFB"/>
      <stop offset="100%" stop-color="#E5E7EB"/>
    </linearGradient>
    <linearGradient id="blackFrame" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%"   stop-color="#111827"/>
      <stop offset="50%"  stop-color="#1F2937"/>
      <stop offset="100%" stop-color="#0F172A"/>
    </linearGradient>
    <linearGradient id="glassSheen" x1="0" y1="0" x2="0.5" y2="1">
      <stop offset="0%"   stop-color="#FFFFFF" stop-opacity="0.22"/>
      <stop offset="40%"  stop-color="#FFFFFF" stop-opacity="0.04"/>
      <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="shelfGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stop-color="#CBD5E1"/>
      <stop offset="50%"  stop-color="#94A3B8"/>
      <stop offset="100%" stop-color="#475569"/>
    </linearGradient>
    <linearGradient id="ledScreen" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%"   stop-color="#FDBA74"/>
      <stop offset="35%"  stop-color="#F97316"/>
      <stop offset="75%"  stop-color="#B45309"/>
      <stop offset="100%" stop-color="#1F2937"/>
    </linearGradient>
    <radialGradient id="adSpotlight" cx="50%" cy="45%" r="60%">
      <stop offset="0%"   stop-color="#FDE68A" stop-opacity="0.6"/>
      <stop offset="100%" stop-color="#FDE68A" stop-opacity="0"/>
    </radialGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="3" result="blur"/>
      <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
    <filter id="glowSm" x="-70%" y="-70%" width="240%" height="240%">
      <feGaussianBlur stdDeviation="1.5" result="blur"/>
      <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
    <filter id="bodyShadow" x="-10%" y="-10%" width="120%" height="120%">
      <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="#000" flood-opacity="0.15"/>
    </filter>
  </defs>`;
};

/* ─── Top box (above machine) ─────────────────────────────────────────── */
S.buildTopBox = function(type, x, y, w, h){
  if(type === 'advertising'){
    return `<g id="top-box-ad">
      <rect x="${x+2}" y="${y}" width="${w-4}" height="${h}" rx="4" fill="#0A0F1C"/>
      <rect x="${x+5}" y="${y+3}" width="${w-10}" height="${h-6}" rx="2" fill="url(#ledScreen)"/>
      <rect x="${x+5}" y="${y+3}" width="${w-10}" height="${h-6}" rx="2" fill="url(#adSpotlight)"/>
      <text x="${x+w/2}" y="${y+h*0.48}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="13" font-weight="900"
        letter-spacing="2" fill="#FFFFFF">DIGITAL AD</text>
      <text x="${x+w/2}" y="${y+h*0.72}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="7" font-weight="700"
        letter-spacing="2" fill="#FEF3C7">HD · LIVE · MANAGED</text>
      <circle cx="${x+w-14}" cy="${y+10}" r="2.5" fill="#EF4444" filter="url(#glowSm)"/>
    </g>`;
  }
  if(type === 'led_top'){
    let out = `<g id="top-box-led">
      <rect x="${x+2}" y="${y}" width="${w-4}" height="${h}" rx="4" fill="#F3F4F6"
        stroke="#D1D5DB" stroke-width="1"/>
      <text x="${x+w/2}" y="${y+h*0.55}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="15" font-weight="900"
        letter-spacing="3" fill="#1F2937">VMFS USA</text>
      <rect x="${x+10}" y="${y+h-9}" width="${w-20}" height="4" rx="2" fill="#1A56DB" filter="url(#glow)"/>`;
    const dots = Math.floor((w-24)/10);
    for(let i=0;i<dots;i++){
      out += `<circle cx="${x+14+i*10}" cy="${y+h-7}" r="1" fill="#FFFFFF" opacity="0.9"/>`;
    }
    out += `</g>`;
    return out;
  }
  if(type === 'top_box'){
    return `<g id="top-box-standard">
      <rect x="${x+2}" y="${y}" width="${w-4}" height="${h}" rx="4" fill="#F9FAFB"
        stroke="#D1D5DB" stroke-width="1"/>
      <rect x="${x+4}" y="${y+3}" width="3" height="${h-6}" fill="#1A56DB"/>
      <text x="${x+w/2}" y="${y+h*0.6}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="16" font-weight="900"
        letter-spacing="3" fill="#1A56DB">VMFS USA</text>
    </g>`;
  }
  return '';
};

/* ─── Outdoor canopy ──────────────────────────────────────────────────── */
S.buildCanopy = function(mX, mY, mW){
  const cX = mX - 14, cW = mW + 28, cH = 18;
  const cY = mY - 18;
  return `<g id="canopy">
    <rect x="${cX}" y="${cY}" width="${cW}" height="${cH}" rx="3" fill="#6B7280"/>
    <rect x="${cX}" y="${cY}" width="${cW}" height="3" fill="#9CA3AF"/>
    <rect x="${cX}" y="${cY+cH-3}" width="${cW}" height="3" fill="#374151"/>
    <rect x="${mX+mW*0.15}" y="${cY+cH}" width="6" height="5" fill="#4B5563"/>
    <rect x="${mX+mW*0.81}" y="${cY+cH}" width="6" height="5" fill="#4B5563"/>
    <rect x="${mX-3}" y="${mY+4}" width="2" height="22" fill="#60A5FA" opacity="0.4"/>
    <rect x="${mX+mW+1}" y="${mY+4}" width="2" height="22" fill="#60A5FA" opacity="0.4"/>
  </g>`;
};

/* ─── Camera module ───────────────────────────────────────────────────── */
S.buildCamera = function(cx, cy){
  return `<g id="camera">
    <rect x="${cx-16}" y="${cy-6}" width="32" height="14" rx="2" fill="#1F2937"
      stroke="#4B5563" stroke-width="0.5"/>
    <circle cx="${cx-4}" cy="${cy+1}" r="5" fill="#0F172A"/>
    <circle cx="${cx-4}" cy="${cy+1}" r="5" fill="none" stroke="#4B5563" stroke-width="0.5"/>
    <circle cx="${cx-4}" cy="${cy+1}" r="3.5" fill="#1E293B"/>
    <circle cx="${cx-4}" cy="${cy+1}" r="2" fill="#0F172A"/>
    <circle cx="${cx-5}" cy="${cy}" r="0.7" fill="rgba(255,255,255,0.3)"/>
    <circle cx="${cx+8}" cy="${cy-2}" r="1.8" fill="#10B981" filter="url(#glowSm)"/>
    <circle cx="${cx+7}" cy="${cy+4}" r="0.9" fill="#DC2626" opacity="0.7"/>
    <circle cx="${cx+11}" cy="${cy+2}" r="0.9" fill="#DC2626" opacity="0.5"/>
  </g>`;
};

/* ─── Side LED strips ─────────────────────────────────────────────────── */
S.buildSideLEDs = function(mX, mY, mW, mH){
  return `<g id="side-leds">
    <rect x="${mX-3}" y="${mY+20}" width="2" height="${mH-80}" rx="1"
      fill="#3B82F6" opacity="0.75" filter="url(#glow)"/>
    <rect x="${mX+mW+1}" y="${mY+20}" width="2" height="${mH-80}" rx="1"
      fill="#3B82F6" opacity="0.75" filter="url(#glow)"/>
  </g>`;
};

/* ─── IP65 badge ──────────────────────────────────────────────────────── */
S.buildIP65Badge = function(x, y){
  return `<g id="ip65">
    <rect x="${x}" y="${y}" width="36" height="14" rx="2"
      fill="#FFFFFF" stroke="#1A56DB" stroke-width="0.8"/>
    <text x="${x+18}" y="${y+10}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="7" font-weight="900"
      fill="#1A56DB" letter-spacing="0.5">IP65</text>
  </g>`;
};

/* ─── Connectivity badges (WiFi / 4G / SAT) ───────────────────────────── */
S.buildConnectivityBadges = function(state, x, y){
  const conn = state.connectivity || [];
  if(!conn.length) return '';

  let out = `<g id="conn-badges">`;
  let ix = x;

  if(conn.includes('wifi')){
    out += `<g>
      <rect x="${ix}" y="${y}" width="22" height="16" rx="2.5" fill="#FFFFFF"
        stroke="#1A56DB" stroke-width="0.8"/>
      <path d="M${ix+5.5},${y+9} Q${ix+11},${y+6} ${ix+16.5},${y+9}" fill="none" stroke="#1A56DB" stroke-width="1" stroke-linecap="round"/>
      <path d="M${ix+7.5},${y+11} Q${ix+11},${y+9} ${ix+14.5},${y+11}" fill="none" stroke="#1A56DB" stroke-width="0.9" stroke-linecap="round"/>
      <circle cx="${ix+11}" cy="${y+12.5}" r="0.8" fill="#1A56DB"/>
      <text x="${ix+11}" y="${y+5}" text-anchor="middle" font-family="-apple-system,sans-serif"
        font-size="3" font-weight="700" fill="#1A56DB">WIFI</text>
    </g>`;
    ix += 26;
  }
  if(conn.includes('cellular')){
    out += `<g>
      <rect x="${ix}" y="${y}" width="22" height="16" rx="2.5" fill="#FFFFFF"
        stroke="#059669" stroke-width="0.8"/>
      <rect x="${ix+4}" y="${y+11}" width="2" height="3" fill="#059669"/>
      <rect x="${ix+7.5}" y="${y+9}" width="2" height="5" fill="#059669"/>
      <rect x="${ix+11}" y="${y+7}" width="2" height="7" fill="#059669"/>
      <rect x="${ix+14.5}" y="${y+5}" width="2" height="9" fill="#059669"/>
      <text x="${ix+11}" y="${y+4}" text-anchor="middle" font-family="-apple-system,sans-serif"
        font-size="3" font-weight="700" fill="#059669">4G</text>
    </g>`;
    ix += 26;
  }
  if(conn.includes('satellite')){
    out += `<g>
      <rect x="${ix}" y="${y}" width="22" height="16" rx="2.5" fill="#FFFFFF"
        stroke="#7C3AED" stroke-width="0.8"/>
      <path d="M${ix+5},${y+11} Q${ix+11},${y+7} ${ix+17},${y+11} L${ix+15},${y+13} L${ix+7},${y+13} Z" fill="#7C3AED"/>
      <line x1="${ix+11}" y1="${y+9}" x2="${ix+11}" y2="${y+13}" stroke="#7C3AED" stroke-width="0.8"/>
      <circle cx="${ix+11}" cy="${y+6}" r="1" fill="#7C3AED"/>
      <path d="M${ix+13},${y+5} Q${ix+14.5},${y+4} ${ix+16},${y+5}" fill="none" stroke="#7C3AED" stroke-width="0.5"/>
      <text x="${ix+11}" y="${y+4}" text-anchor="middle" font-family="-apple-system,sans-serif"
        font-size="3" font-weight="700" fill="#7C3AED">SAT</text>
    </g>`;
    ix += 26;
  }
  out += `</g>`;
  return out;
};

/* ─── Feet / wheels ───────────────────────────────────────────────────── */
S.buildFeet = function(mX, baseY, mW, withWheels){
  if(withWheels){
    let out = `<g id="feet">`;
    [[mX+14, baseY+3],[mX+mW-14, baseY+3]].forEach(([cx,cy])=>{
      out += `<rect x="${cx-5}" y="${cy}" width="10" height="8" rx="1.5" fill="#374151"/>`;
      out += `<circle cx="${cx}" cy="${cy+8}" r="4" fill="#1F2937" stroke="#6B7280" stroke-width="0.5"/>`;
      out += `<circle cx="${cx}" cy="${cy+8}" r="1.5" fill="#4B5563"/>`;
    });
    out += `</g>`;
    return out;
  }
  return `<g id="feet">
    <rect x="${mX+10}" y="${baseY}" width="22" height="5" rx="1.5" fill="#374151"/>
    <rect x="${mX+mW-32}" y="${baseY}" width="22" height="5" rx="1.5" fill="#374151"/>
    <rect x="${mX+16}" y="${baseY+5}" width="10" height="4" rx="1" fill="#1F2937"/>
    <rect x="${mX+mW-26}" y="${baseY+5}" width="10" height="4" rx="1" fill="#1F2937"/>
  </g>`;
};

/* ─── VMFS logo (small + large) ───────────────────────────────────────── */
S.buildVmfsLogo = function(x, y, size, color){
  return `<g>
    <text x="${x}" y="${y}" font-family="-apple-system,sans-serif"
      font-size="${size}" font-weight="900" letter-spacing="2" fill="${color||'#1F2937'}">VMFS USA</text>
    <text x="${x}" y="${y+size*0.7}" font-family="-apple-system,sans-serif"
      font-size="${size*0.4}" font-weight="600" letter-spacing="1.5"
      fill="${color||'#6B7280'}" opacity="0.7">VENDING MACHINES</text>
  </g>`;
};

S.buildVmfsLogoBig = function(cx, y, color){
  const r = 10;
  return `<g>
    <circle cx="${cx-44}" cy="${y+4}" r="${r}" fill="none" stroke="${color}" stroke-width="1.5"/>
    <text x="${cx-44}" y="${y+8}" text-anchor="middle" font-family="-apple-system,sans-serif"
      font-size="11" font-weight="900" fill="${color}">V</text>
    <text x="${cx-28}" y="${y+4}" font-family="-apple-system,sans-serif"
      font-size="14" font-weight="900" letter-spacing="2.5" fill="${color}">VMFS USA</text>
    <text x="${cx-28}" y="${y+16}" font-family="-apple-system,sans-serif"
      font-size="6.5" font-weight="700" letter-spacing="2" fill="${color}" opacity="0.75">VENDING MACHINES</text>
  </g>`;
};

/* ─── Payment row (compact horizontal — card / cash / QR) ─────────────── */
S.buildPaymentRow = function(state, x, y, w){
  const hasCard = (state.payments||[]).includes('tap_swipe');
  const hasCash = (state.payments||[]).includes('bill_coin');
  const hasQR   = (state.payments||[]).includes('codes');

  const rowH = 28;
  let out = `<g id="payment-row">`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${rowH}" rx="2" fill="#F3F4F6"
    stroke="#E5E7EB" stroke-width="0.6"/>`;

  const methods = [];
  if(hasCard) methods.push('card');
  if(hasCash) methods.push('cash');
  if(hasQR)   methods.push('qr');
  if(methods.length === 0) methods.push('qr');

  const slotW = (w - 6 - (methods.length-1)*3) / methods.length;
  let ix = x + 3;

  methods.forEach(m=>{
    if(m === 'card'){
      out += `<rect x="${ix}" y="${y+3}" width="${slotW}" height="${rowH-6}" rx="2" fill="#111827"/>`;
      out += `<rect x="${ix+2}" y="${y+6}" width="${slotW-4}" height="${rowH*0.4}" rx="1" fill="#1F2937"
        stroke="#4A9EFF" stroke-width="0.5" opacity="0.8"/>`;
      out += `<rect x="${ix+3}" y="${y+8}" width="${slotW-6}" height="2" fill="#3B82F6" opacity="0.6"/>`;
      out += `<text x="${ix+slotW/2}" y="${y+rowH-4}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
        fill="#4B5563">TAP · CARD</text>`;
    } else if(m === 'cash'){
      out += `<rect x="${ix}" y="${y+3}" width="${slotW}" height="${rowH-6}" rx="2" fill="#FBBF24"/>`;
      out += `<rect x="${ix+3}" y="${y+6}" width="${slotW-6}" height="3" rx="1" fill="#78350F"/>`;
      out += `<circle cx="${ix+slotW*0.3}" cy="${y+rowH*0.65}" r="2.5" fill="#78350F"/>`;
      out += `<rect x="${ix+slotW*0.28}" y="${y+rowH*0.58}" width="0.8" height="4.5" fill="#FBBF24"/>`;
      out += `<text x="${ix+slotW*0.72}" y="${y+rowH*0.72}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
        fill="#78350F">BILL/$</text>`;
    } else if(m === 'qr'){
      out += `<rect x="${ix}" y="${y+3}" width="${slotW}" height="${rowH-6}" rx="2" fill="#111827"/>`;
      const qcx = ix + slotW/2, qcy = y + rowH*0.42;
      for(let gx=0;gx<3;gx++){
        for(let gy=0;gy<3;gy++){
          const size = (gx===1 && gy===1) ? 0 : 1.5;
          if(size > 0){
            out += `<rect x="${qcx-5+gx*3.5}" y="${qcy-5+gy*3.5}" width="${size}" height="${size}"
              fill="#3B82F6" opacity="0.9"/>`;
          }
        }
      }
      out += `<text x="${ix+slotW/2}" y="${y+rowH-4}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
        fill="#D1D5DB">QR SCAN</text>`;
    }
    ix += slotW + 3;
  });

  out += `</g>`;
  return out;
};

/* ─── Coin return cup ─────────────────────────────────────────────────── */
S.buildCoinReturn = function(x, y, w){
  const cupW = w * 0.6;
  const cupX = x + (w - cupW)/2;
  return `<g id="coin-return">
    <path d="M${cupX},${y} L${cupX+cupW},${y} L${cupX+cupW-3},${y+9} L${cupX+3},${y+9} Z"
      fill="#1F2937" stroke="#374151" stroke-width="0.5"/>
    <path d="M${cupX+2},${y+2} L${cupX+cupW-2},${y+2} L${cupX+cupW-4},${y+7} L${cupX+4},${y+7} Z"
      fill="#0A0F1C"/>
    <text x="${cupX+cupW/2}" y="${y+15}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
      letter-spacing="0.8" fill="#6B7280">COIN RETURN</text>
  </g>`;
};

/* ─── ID scanner panel (age verify — ID + NFC + face) ─────────────────── */
S.buildIDScannerPanel = function(x, y, w, h){
  let out = `<g id="id-scanner">`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="4"
    fill="#FEF3C7" stroke="#DC2626" stroke-width="1"/>`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="10" rx="4" fill="#DC2626"/>`;
  out += `<rect x="${x}" y="${y+5}" width="${w}" height="5" fill="#DC2626"/>`;
  out += `<text x="${x+w/2}" y="${y+7.5}" text-anchor="middle" font-family="-apple-system,sans-serif"
    font-size="5.5" font-weight="900" letter-spacing="1.5" fill="#FFFFFF">AGE VERIFICATION · 21+</text>`;

  const modW = (w - 12) / 3;
  const modY = y + 13;
  const modH = h - 16;

  /* 1. ID slot */
  let mx = x + 3;
  out += `<rect x="${mx}" y="${modY}" width="${modW}" height="${modH}" rx="2" fill="#FFFFFF"
    stroke="#DC2626" stroke-width="0.5"/>`;
  out += `<rect x="${mx+3}" y="${modY+3}" width="${modW-6}" height="${modH*0.5}" rx="1.5"
    fill="none" stroke="#DC2626" stroke-width="0.8"/>`;
  out += `<rect x="${mx+5}" y="${modY+5}" width="${modW*0.35}" height="${modH*0.3}" fill="#FCA5A5"/>`;
  out += `<line x1="${mx+5+modW*0.4}" y1="${modY+5}" x2="${mx+modW-5}" y2="${modY+5}" stroke="#DC2626" stroke-width="0.5"/>`;
  out += `<line x1="${mx+5+modW*0.4}" y1="${modY+8}" x2="${mx+modW-7}" y2="${modY+8}" stroke="#DC2626" stroke-width="0.4" opacity="0.7"/>`;
  out += `<line x1="${mx+5+modW*0.4}" y1="${modY+11}" x2="${mx+modW-9}" y2="${modY+11}" stroke="#DC2626" stroke-width="0.4" opacity="0.5"/>`;
  out += `<text x="${mx+modW/2}" y="${modY+modH-3}" text-anchor="middle" font-family="-apple-system,sans-serif"
    font-size="4" font-weight="700" fill="#92400E">SCAN ID</text>`;

  /* 2. NFC tap */
  mx = x + 3 + modW + 3;
  out += `<rect x="${mx}" y="${modY}" width="${modW}" height="${modH}" rx="2" fill="#FFFFFF"
    stroke="#DC2626" stroke-width="0.5"/>`;
  const nfcCx = mx + modW/2, nfcCy = modY + modH*0.42;
  out += `<path d="M${nfcCx-5},${nfcCy-3} Q${nfcCx-1},${nfcCy} ${nfcCx-5},${nfcCy+3}" fill="none" stroke="#DC2626" stroke-width="1.2" stroke-linecap="round"/>`;
  out += `<path d="M${nfcCx-3},${nfcCy-5} Q${nfcCx+2},${nfcCy} ${nfcCx-3},${nfcCy+5}" fill="none" stroke="#DC2626" stroke-width="1" stroke-linecap="round" opacity="0.8"/>`;
  out += `<path d="M${nfcCx-1},${nfcCy-7} Q${nfcCx+5},${nfcCy} ${nfcCx-1},${nfcCy+7}" fill="none" stroke="#DC2626" stroke-width="0.8" stroke-linecap="round" opacity="0.5"/>`;
  out += `<text x="${mx+modW/2}" y="${modY+modH-3}" text-anchor="middle" font-family="-apple-system,sans-serif"
    font-size="4" font-weight="700" fill="#92400E">NFC TAP</text>`;

  /* 3. Face scan */
  mx = x + 3 + (modW + 3)*2;
  out += `<rect x="${mx}" y="${modY}" width="${modW}" height="${modH}" rx="2" fill="#FFFFFF"
    stroke="#DC2626" stroke-width="0.5"/>`;
  const fcx = mx + modW/2, fcy = modY + modH*0.42;
  out += `<ellipse cx="${fcx}" cy="${fcy}" rx="4" ry="5" fill="none" stroke="#DC2626" stroke-width="1"/>`;
  out += `<ellipse cx="${fcx}" cy="${fcy+5}" rx="6" ry="2.5" fill="none" stroke="#DC2626" stroke-width="0.8"/>`;
  out += `<path d="M${mx+3},${modY+3} L${mx+1},${modY+3} L${mx+1},${modY+6}" fill="none" stroke="#DC2626" stroke-width="0.8"/>`;
  out += `<path d="M${mx+modW-3},${modY+3} L${mx+modW-1},${modY+3} L${mx+modW-1},${modY+6}" fill="none" stroke="#DC2626" stroke-width="0.8"/>`;
  out += `<text x="${mx+modW/2}" y="${modY+modH-3}" text-anchor="middle" font-family="-apple-system,sans-serif"
    font-size="4" font-weight="700" fill="#92400E">FACE</text>`;

  out += `</g>`;
  return out;
};

/* ─── Delivery door (PUSH flap) ───────────────────────────────────────── */
S.buildDeliveryDoor = function(x, y, w, h, isBig){
  const doorW = isBig ? w - 4 : Math.min(w * 0.7, 120);
  const doorX = x + (w - doorW)/2;
  let out = `<g id="delivery-door">`;
  out += `<rect x="${doorX}" y="${y}" width="${doorW}" height="${h}" rx="3"
    fill="#1F2937"/>`;
  out += `<rect x="${doorX+3}" y="${y+3}" width="${doorW-6}" height="${h-6}" rx="2"
    fill="#374151" stroke="#4B5563" stroke-width="0.5"/>`;
  out += `<rect x="${doorX+3}" y="${y+3}" width="${doorW-6}" height="${(h-6)*0.35}" rx="2"
    fill="rgba(255,255,255,0.08)"/>`;
  out += `<text x="${doorX+doorW/2}" y="${y+h*0.75}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="9" font-weight="800"
    letter-spacing="3" fill="#E5E7EB">PUSH</text>`;
  out += `<rect x="${doorX+doorW/2-10}" y="${y+h*0.42}" width="20" height="3" rx="1.5"
    fill="#6B7280"/>`;
  out += `</g>`;
  return out;
};

/* ─── Expose ──────────────────────────────────────────────────────────── */
window.VMFS.shared = S;

})();
